import { useState } from "react";
import { Link, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, Workflow, Eye, CheckCircle2 } from "lucide-react";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite") || searchParams.get("token");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "property_manager", company: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Public self-service registration is disabled. Only NTN-issued invite links
  // (carrying an ?invite= or ?token= query param) may reach this page.
  if (!inviteToken) {
    return <Navigate to="/request-information" replace />;
  }

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const res = await register(form);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome, ${res.user.name}`);
      navigate("/dashboard");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans grid grid-cols-1 lg:grid-cols-2" data-testid="register-page">
      <div className="hidden lg:flex relative bg-slate-900 text-white p-12 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 [background-image:radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.22),transparent_55%),radial-gradient(circle_at_80%_85%,rgba(139,92,246,0.14),transparent_55%)] pointer-events-none" />
        <div className="absolute inset-0 [background-image:linear-gradient(to_right,rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.04)_1px,transparent_1px)] [background-size:42px_42px] [mask-image:radial-gradient(ellipse_at_top,black,transparent_75%)] pointer-events-none" />

        <div className="relative z-10"><Logo light /></div>

        <div className="relative z-10 max-w-md space-y-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] tracking-[0.18em] uppercase font-semibold mb-5 bg-white/10 text-blue-200">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-300" />
              Create Account
            </div>
            <h2 className="font-serif text-4xl tracking-tight leading-tight">
              Order, track, and decide on every applicant — <span className="text-blue-300 italic">in one workflow.</span>
            </h2>
            <p className="mt-4 text-white/70 text-sm leading-relaxed">
              Join property managers using NTN Verify Hub to move applicants from submission to decision-ready in days, not weeks.
            </p>
          </div>

          <ul className="space-y-3 text-sm text-white/80">
            {[
              { icon: ShieldCheck, label: "FCRA-compliant verification workflows" },
              { icon: Workflow,    label: "Department queues for high-volume processing" },
              { icon: Eye,         label: "Real-time visibility for property managers" },
            ].map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-md bg-white/10 flex items-center justify-center">
                  <Icon className="h-4 w-4 text-blue-300" />
                </div>
                {label}
              </li>
            ))}
          </ul>
        </div>

        <div className="relative z-10 font-mono text-[10px] tracking-widest uppercase text-white/40">
          NTN · Established 1980
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10"><Logo /></div>

          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] tracking-[0.18em] uppercase font-semibold mb-5 bg-blue-50 text-blue-700">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
            Open Account
          </div>

          <h1 className="font-serif text-4xl text-slate-900 tracking-tight leading-tight mb-2">Create your account.</h1>
          <p className="text-slate-600 text-sm mb-8">
            Already a member?{" "}
            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-700" data-testid="link-to-login">Sign in</Link>
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-600">Account type</Label>
              <Select value={form.role} onValueChange={(v) => onChange("role", v)}>
                <SelectTrigger className="mt-2 h-11 rounded-md bg-white border-slate-300" data-testid="register-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="property_manager" data-testid="role-pm">Property Manager / Landlord</SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1.5 text-[11px] text-slate-500">
                Accounts are provisioned by NTN. Applicant access is provided through the application link your property manager sends you.
              </p>
            </div>
            <div>
              <Label htmlFor="name" className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-600">Full name</Label>
              <Input id="name" required value={form.name} onChange={(e) => onChange("name", e.target.value)}
                className="mt-2 h-11 rounded-md bg-white border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-testid="register-name" />
            </div>
            {form.role !== "applicant" && (
              <div>
                <Label htmlFor="company" className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-600">Company</Label>
                <Input id="company" value={form.company} onChange={(e) => onChange("company", e.target.value)}
                  className="mt-2 h-11 rounded-md bg-white border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-testid="register-company" />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-600">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => onChange("email", e.target.value)}
                className="mt-2 h-11 rounded-md bg-white border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-testid="register-email" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-600">Password</Label>
              <Input id="password" type="password" required minLength={10} value={form.password} onChange={(e) => onChange("password", e.target.value)}
                className="mt-2 h-11 rounded-md bg-white border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-testid="register-password" />
              <p className="mt-1.5 text-[11px] text-slate-500 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                Min 10 characters, including upper/lower, digit, and a symbol.
              </p>
            </div>
            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-md" data-testid="register-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={loading}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-semibold shadow-sm shadow-blue-600/20"
              data-testid="register-submit">
              {loading ? "Creating account…" : <>Create account <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
